import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';

import { CAROUSEL_CONTENT_BANNER_BREAKPOINTS, FALLBACK_LOGO_SRC } from '@ppg/core/constants';
import { StaticContentService } from '@ppg/core/content';
import {
  ContainerContentBannerTheme,
  ContainerContentBannerPresentation,
  BackgroundClass,
  ClientSiteSettingsKeys,
} from '@ppg/core/enums';
import { ContainerContentBannerContent } from '@ppg/core/models';
import { HorizontalDividerComponent } from '@ppg/shared/components';

import { CarouselPresentationContentBannerComponent } from './presentations/carousel/carousel-presentation.component';
import { Horizontal5050PresentationContentBannerComponent } from './presentations/horizontal-50-50/horizontal-50-50-presentation.component';
import { HorizontalShortPresentationContentBannerComponent } from './presentations/horizontal-short/horizontal-short-presentation.component';
import { HorizontalStandardPresentationContentBannerComponent } from './presentations/horizontal-standard/horizontal-standard-presentation.component';
import { HorizontalTallPresentationContentBannerComponent } from './presentations/horizontal-tall/horizontal-tall-presentation.component';
import { LargePresentationContentBannerComponent } from './presentations/large/large-presentation.component';
import { SmallPresentationContentBannerComponent } from './presentations/small/small-presentation.component';

@Component({
  selector: 'ppg-container-content-banner-component',
  standalone: true,
  templateUrl: './container-content-banner.component.html',
  styleUrl: './container-content-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    HorizontalDividerComponent,
    LargePresentationContentBannerComponent,
    SmallPresentationContentBannerComponent,
    HorizontalTallPresentationContentBannerComponent,
    HorizontalStandardPresentationContentBannerComponent,
    HorizontalShortPresentationContentBannerComponent,
    Horizontal5050PresentationContentBannerComponent,
    CarouselPresentationContentBannerComponent,
    CarouselModule,
  ],
})
export class ContainerContentBannerComponent {
  readonly content = input.required<ContainerContentBannerContent>();

  // logoUrl can be set from component render
  // default in html if empty is set value from StaticAsset - BrandingAssetsHeaderLogo
  readonly logoUrl = input<string>();

  private readonly staticContentService = inject(StaticContentService);

  readonly headline = computed(() => this.content().headline ?? '');
  readonly subheading = computed(() => this.content().subheading ?? '');
  readonly presentation = computed(() => this.content().presentation ?? '');
  readonly theme = computed(() => this.content().theme);
  readonly cards = computed(() => this.content().cards.filter((item) => item) ?? []);
  readonly pageSectionBackground = computed(() => this.content().backgroundColorRGB ?? '');

  readonly ContainerContentBannerPresentation = ContainerContentBannerPresentation;
  readonly responsiveOptions = CAROUSEL_CONTENT_BANNER_BREAKPOINTS;
  readonly logo = computed(() => this.logoUrl() ?? this.brandingAssetsHeaderLogo());

  isDefaultBackground = computed(() => this.theme() === ContainerContentBannerTheme.DEFAULT);
  isTransparentBackground = computed(() => this.theme() === ContainerContentBannerTheme.TRANSPARENT);
  isLightBackground = computed(() => this.theme() === ContainerContentBannerTheme.LIGHT);
  isDarkBackground = computed(() => this.theme() === ContainerContentBannerTheme.DARK);
  isVerticalPresentation = computed(
    () => this.ContainerContentBannerPresentation.LARGE || this.ContainerContentBannerPresentation.SMALL,
  );

  getCardBackgroundClass(orderTheme: number | undefined, currentCardIndex: number): string {
    return orderTheme === undefined
      ? this.getBackgroundFromThemeValue()
      : this.getBackgroundFromThemeOrder(currentCardIndex, orderTheme);
  }

  private readonly brandingAssetsHeaderLogo = computed(
    () =>
      this.staticContentService.getStaticAsset(ClientSiteSettingsKeys.BrandingAssetsHeaderLogo)?.url ??
      FALLBACK_LOGO_SRC ??
      '',
  );

  private getBackgroundFromThemeOrder(currentCardIndex: number, orderTheme: number) {
    const backgroundClass = [
      [BackgroundClass.LIGHT, BackgroundClass.DARK, BackgroundClass.TRANSPARENT],
      [BackgroundClass.DARK, BackgroundClass.TRANSPARENT, BackgroundClass.LIGHT],
      [BackgroundClass.TRANSPARENT, BackgroundClass.LIGHT, BackgroundClass.DARK],
    ];

    return backgroundClass[currentCardIndex % 3][orderTheme] || BackgroundClass.DEFAULT;
  }

  private getBackgroundFromThemeValue(): string {
    return this.isDarkBackground()
      ? BackgroundClass.DARK
      : this.isLightBackground()
        ? BackgroundClass.LIGHT
        : this.isTransparentBackground()
          ? BackgroundClass.TRANSPARENT
          : BackgroundClass.DEFAULT;
  }
}
